import {
  AppBar,
  Toolbar,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Link,
} from '@mui/material'
import { FC, ReactNode, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import NotificationsIcon from '@mui/icons-material/Notifications'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import PersonIcon from '@mui/icons-material/Person'
import { useAuth } from '@/hooks/useAuth'
import { useNotificacoes } from '@/hooks/useNotificacoes'
import { getInitials } from '@/utils/formatters'
import { tokens } from '@/theme'
import { SeletorCanalGlobal } from './SeletorCanalGlobal'
import { rotuloDaRota } from './breadcrumbs'
import { ehConsultor } from './menu'

interface TopBarProps {
  sidebarCollapsed: boolean
  /**
   * Slot à direita, antes do sino de notificações (Onda 0, item 5): é aqui que o
   * coordenador pluga o `SeletorCanalGlobal` do A02. Este componente não sabe o que
   * vai dentro — só reserva o lugar.
   */
  acaoDireita?: ReactNode
  children?: ReactNode
}

/** Breadcrumb `Início › <Página>` derivado da rota (mapa em breadcrumbs.ts). */
const BreadcrumbDaRota: FC = () => {
  const { usuario } = useAuth()
  const rotulo = rotuloDaRota(useLocation().pathname, ehConsultor(usuario?.permissoes))
  return (
    <Box
      component="nav"
      aria-label="breadcrumb"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap' }}
    >
      <Link
        component={RouterLink}
        to="/dashboard"
        underline="hover"
        sx={{ color: tokens.textSecondary, fontSize: '0.8125rem', fontWeight: 500 }}
      >
        Início
      </Link>
      {rotulo && (
        <>
          <Typography sx={{ color: '#BDBDBD', fontSize: '0.8125rem' }} aria-hidden>
            ›
          </Typography>
          <Typography
            sx={{ color: tokens.brandInk, fontSize: '0.8125rem', fontWeight: 600 }}
          >
            {rotulo}
          </Typography>
        </>
      )}
    </Box>
  )
}

export const TopBar: FC<TopBarProps> = ({ sidebarCollapsed, acaoDireita, children }) => {
  const navigate = useNavigate()
  const { usuario, logout } = useAuth()
  const { naoLidas } = useNotificacoes()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [searchOpen, setSearchOpen] = useState(false)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    handleMenuClose()
    await logout()
  }

  const handleNotificationClick = () => {
    navigate('/notificacoes')
  }

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: tokens.surfaceCard,
        color: tokens.brandInk,
        boxShadow: 'none',
        borderBottom: `1px solid ${tokens.divider}`,
        zIndex: (theme) => theme.zIndex.drawer + 1,
        marginLeft: sidebarCollapsed ? '64px' : '240px',
        width: `calc(100% - ${sidebarCollapsed ? '64px' : '240px'})`,
        transition: 'margin 0.25s ease, width 0.25s ease',
      }}
    >
      <Toolbar sx={{ paddingX: 3, minHeight: '64px !important' }}>
        {/* Breadcrumb + busca + escopo (Fase 6, item 20: o isolamento escrito na tela) */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <BreadcrumbDaRota />
          {searchOpen ? (
            <TextField
              placeholder="Buscar clientes, oportunidades..."
              variant="outlined"
              size="small"
              autoFocus
              onBlur={() => setSearchOpen(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: tokens.textSecondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                },
              }}
            />
          ) : (
            <IconButton
              size="small"
              onClick={() => setSearchOpen(true)}
              sx={{
                color: tokens.textSecondary,
                '&:hover': { color: tokens.brandInk, backgroundColor: '#F7F7F8' },
              }}
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {/* Slot do seletor de canal global (A02) e afins */}
          <SeletorCanalGlobal />
          {acaoDireita}
          {children}

          {/* Notifications */}
          <IconButton
            onClick={handleNotificationClick}
            size="small"
            sx={{
              color: tokens.textSecondary,
              '&:hover': { color: tokens.brandInk, backgroundColor: '#F7F7F8' },
            }}
          >
            <Badge
              badgeContent={naoLidas}
              color="error"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}
            >
              <NotificationsIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 20, alignSelf: 'center' }} />

          {/* User avatar */}
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{ padding: 0.5 }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                backgroundColor: tokens.brandPrimary,
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '-0.01em',
              }}
            >
              {getInitials(usuario?.nome)}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 200,
                  mt: 0.5,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: tokens.brandInk, letterSpacing: '-0.01em' }}
              >
                {usuario?.nome}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: tokens.textSecondary, letterSpacing: '-0.01em' }}
              >
                {usuario?.email}
              </Typography>
            </Box>

            <Divider />

            <MenuItem
              onClick={() => { navigate('/perfil'); handleMenuClose() }}
              sx={{ gap: 1.5, fontSize: '0.875rem' }}
            >
              <PersonIcon sx={{ fontSize: 16, color: tokens.textSecondary }} />
              Perfil
            </MenuItem>

            <MenuItem
              onClick={() => { navigate('/configuracoes'); handleMenuClose() }}
              sx={{ gap: 1.5, fontSize: '0.875rem' }}
            >
              <SettingsIcon sx={{ fontSize: 16, color: tokens.textSecondary }} />
              Configurações
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={handleLogout}
              sx={{ gap: 1.5, fontSize: '0.875rem', color: tokens.statusDanger }}
            >
              <LogoutIcon sx={{ fontSize: 16 }} />
              Sair
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  )
}
